import { PageHead } from '@components/PageHead'
import { VaultItemFormView } from '@features/vault/components/VaultItemFormView'
import { useFolders } from '@features/vault/hooks/useFolders'
import { useVaultDetail, type UseVaultDetail } from '@features/vault/hooks/useVaultDetail'
import { useVaultItemForm } from '@features/vault/hooks/useVaultItemForm'
import type { VaultItem } from '@features/vault/types'
import { Notice } from '@shared/ui/Notice'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * 既存 Vault アイテム編集画面 (F4、xlsx No.28 後半)。
 *
 * `useVaultDetail` で item を fetch → 取得後に `useVaultItemForm({ initialItem })`
 * で form を初期化 → `VaultItemFormView` (新規と共通) で描画。
 * form card の下に削除 (左) / 保存 (右) のアクションを置く。削除は確認ダイアログ経由。
 */

export interface VaultEditPageProps {
  id: string
}

const FORM_ID = 'vault-edit-form'

const goVault = (): void => {
  window.location.hash = 'vault'
}

const InnerEditForm = ({
  item,
  detail,
}: {
  item: VaultItem
  detail: UseVaultDetail
}): JSX.Element => {
  const { t } = useTranslation()
  const { folders } = useFolders()

  const form = useVaultItemForm({
    initialItem: item,
    successHash: `vault/${item.id}`,
  })

  const isConfirming = detail.state.kind === 'confirm-delete'
  const isDeleting = detail.state.kind === 'deleting'
  const isDeleteFlow = isConfirming || isDeleting

  return (
    <div>
      <PageHead
        title={t('vault.edit.title')}
        sub={t('vault.edit.subtitle')}
        actions={
          <button type="button" className="btn btn-secondary" onClick={goVault}>
            <Icon name="arrow-left" aria-hidden="true" />
            {t('vault.edit.back')}
          </button>
        }
      />
      <div className="page-body">
        <VaultItemFormView
          form={form}
          folders={folders}
          formId={FORM_ID}
          footer={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={detail.requestDelete}
                disabled={isDeleteFlow || form.isSubmitting}
                data-testid="vault-edit-delete"
              >
                <Icon name="trash" aria-hidden="true" />
                {t('vault.edit.delete')}
              </button>
              <button
                type="submit"
                form={FORM_ID}
                className="btn btn-primary"
                disabled={form.isSubmitting || isDeleteFlow}
              >
                <Icon name="save" aria-hidden="true" />
                {form.isSubmitting ? t('vault.new.saving') : t('vault.edit.save')}
              </button>
            </>
          }
        />
      </div>

      {isDeleteFlow && (
        <div
          className="vault-delete-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vault-edit-delete-title"
        >
          <div className="vault-delete-dialog">
            <h3 id="vault-edit-delete-title">{t('vault.detail.delete_dialog.title')}</h3>
            <p>{t('vault.detail.delete_dialog.body', { name: item.payload.name })}</p>
            <div className="vault-delete-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={detail.cancelDelete}
                disabled={isDeleting}
              >
                {t('vault.detail.delete_dialog.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void detail.confirmDelete()}
                disabled={isDeleting}
                data-testid="vault-edit-delete-confirm"
              >
                <Icon name="trash" aria-hidden="true" />
                {isDeleting
                  ? t('vault.detail.delete_dialog.deleting')
                  : t('vault.detail.delete_dialog.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const VaultEditPage = ({ id }: VaultEditPageProps): JSX.Element => {
  const { t } = useTranslation()
  const detail = useVaultDetail(id)

  if (detail.state.kind === 'loading') {
    return (
      <div>
        <PageHead title={t('vault.edit.title')} sub={t('vault.edit.subtitle')} />
        <div className="page-body">
          <div className="settings-item-sub">{t('vault.detail.loading')}</div>
        </div>
      </div>
    )
  }

  if (detail.state.kind === 'error') {
    return (
      <div>
        <PageHead title={t('vault.edit.title')} sub={t('vault.edit.subtitle')} />
        <div className="page-body">
          <Notice tone="danger" ico="alert">
            {t(detail.state.messageKey)}
          </Notice>
        </div>
      </div>
    )
  }

  if (
    detail.state.kind !== 'ready' &&
    detail.state.kind !== 'confirm-delete' &&
    detail.state.kind !== 'deleting'
  ) {
    // 'deleted' — 編集対象が無いので vault list に戻す
    window.location.hash = 'vault'

    return <div />
  }

  return <InnerEditForm item={detail.state.item} detail={detail} />
}
